import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { ChevronDown, ChevronUp, CheckCircle, Mail, XCircle } from 'react-feather';
import { RichTextEditor } from '../../../../components/common/RichTextEditor/RichTextEditor';
import { sanitizeHtml } from '../../../../components/common/RichTextEditor/sanitize';
import type {
  AbsenceEmailTemplate,
  AttendanceFollowUpEntry,
} from '../../../../services/api/attendanceFollowUps';

interface FollowUpAccordionRowProps {
  entry: AttendanceFollowUpEntry;
  expanded: boolean;
  onToggle: () => void;
  onSaveEdits: (data: { rendered_subject: string; rendered_body: string }) => Promise<void>;
  onSkipClick: () => void;
  onTemplateOverride: (templateId: string) => Promise<void>;
  onCheckboxToggle?: (entryId: string, checked: boolean) => void;
  checked?: boolean;
  templates: AbsenceEmailTemplate[];
}

const TIER_ORDER = [
  'first_warning',
  'first_warning_with_offer',
  'mid_escalation',
  'deferral_offer',
  'forced_deferral',
];

const TIER_LABELS: Record<string, string> = {
  first_warning: 'First Warning',
  first_warning_with_offer: 'First Warning w/ Offer',
  mid_escalation: 'Mid-Escalation',
  deferral_offer: 'Deferral Offer',
  forced_deferral: 'Forced Deferral',
};

/**
 * MED-738 Level 2 accordion row — single component rendering pending, sent,
 * or skipped follow-ups. Collapsed state shows state icon, session info, and
 * secondary line; expanded state varies by status.
 */
export function FollowUpAccordionRow({
  entry,
  expanded,
  onToggle,
  onSaveEdits,
  onSkipClick,
  onTemplateOverride,
  onCheckboxToggle,
  checked,
  templates,
}: FollowUpAccordionRowProps) {
  const isPending = entry.status === 'pending' || entry.status === 'no_template';
  const isSent = entry.status === 'sent';
  const isSkipped = entry.status === 'skipped';

  const [editSubject, setEditSubject] = useState(entry.rendered_subject ?? '');
  const [editBody, setEditBody] = useState(entry.rendered_body ?? '');
  const [saving, setSaving] = useState(false);
  const [switchingTemplate, setSwitchingTemplate] = useState(false);

  // Keep local edit buffer in sync when the underlying entry changes
  // (e.g. after a template override re-renders subject/body).
  useEffect(() => {
    setEditSubject(entry.rendered_subject ?? '');
    setEditBody(entry.rendered_body ?? '');
  }, [entry.id, entry.rendered_subject, entry.rendered_body]);

  const effectiveTemplateId = entry.override_template_id ?? entry.template_id;
  const suggestedTemplateId = entry.template_id;

  const templateOptions = useMemo(
    () => buildTemplateOptions(templates, suggestedTemplateId),
    [templates, suggestedTemplateId],
  );

  const handleTemplateChange = async (value: string | null) => {
    if (!value || value === effectiveTemplateId) return;

    const hasUnsavedEdits =
      editSubject !== (entry.rendered_subject ?? '') || editBody !== (entry.rendered_body ?? '');

    const doSwitch = async () => {
      setSwitchingTemplate(true);
      try {
        await onTemplateOverride(value);
      } finally {
        setSwitchingTemplate(false);
      }
    };

    if (hasUnsavedEdits) {
      modals.openConfirmModal({
        title: 'Switch template?',
        zIndex: 400,
        children: (
          <Text size="sm">
            Switching templates will discard your unsaved subject and body edits. Continue?
          </Text>
        ),
        labels: { confirm: 'Discard & Switch', cancel: 'Cancel' },
        confirmProps: { color: 'dangerRed' },
        onConfirm: doSwitch,
      });
    } else {
      await doSwitch();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveEdits({ rendered_subject: editSubject, rendered_body: editBody });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdits = () => {
    setEditSubject(entry.rendered_subject ?? '');
    setEditBody(entry.rendered_body ?? '');
  };

  const sessionDate = formatSessionDate(entry.session_date);

  return (
    <Box
      style={{
        border: '1px solid #E8E6E1',
        borderRadius: 8,
        background: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <Group
        gap="sm"
        wrap="nowrap"
        p="sm"
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
      >
        {isPending && onCheckboxToggle && (
          <Checkbox
            checked={!!checked}
            onChange={e => onCheckboxToggle(entry.id, e.currentTarget.checked)}
            onClick={e => e.stopPropagation()}
            color="medleyGreen"
            size="sm"
          />
        )}
        <StatusLeadingIcon status={entry.status} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text fw={600} size="sm">
              Session {entry.session_number}
            </Text>
            <Text size="xs" c="dimmed">
              {sessionDate}
            </Text>
          </Group>
          <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
            {buildSecondaryLine(entry)}
          </Text>
        </Box>
        <Group gap={6} wrap="nowrap">
          <StatusPill status={entry.status} />
          {isPending && (
            <Button
              size="compact-xs"
              variant="default"
              onClick={e => {
                e.stopPropagation();
                onSkipClick();
              }}
            >
              Skip
            </Button>
          )}
          <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Toggle accordion">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </ActionIcon>
        </Group>
      </Group>

      {expanded && (
        <Box px="md" pb="md">
          <Divider mb="sm" />
          {isPending && (
            <Stack gap="sm">
              {entry.coach_name && (
                <Text size="xs" c="dimmed">
                  CC: {entry.coach_name}
                  {entry.coach_email ? ` (${entry.coach_email})` : ''}
                </Text>
              )}
              <Select
                label="TEMPLATE"
                data={templateOptions}
                value={effectiveTemplateId}
                onChange={handleTemplateChange}
                disabled={switchingTemplate}
                allowDeselect={false}
                comboboxProps={{ zIndex: 1000 }}
              />
              <TextInput
                label="SUBJECT"
                value={editSubject}
                onChange={e => setEditSubject(e.currentTarget.value)}
              />
              <Box>
                <Text size="xs" fw={600} c="dimmed" mb={6} tt="uppercase">
                  Body
                </Text>
                <RichTextEditor value={editBody} onChange={setEditBody} minHeight={180} />
              </Box>
              <Group gap="xs" mt="xs">
                <Button color="medleyGreen" onClick={handleSave} loading={saving}>
                  Save
                </Button>
                <Button variant="default" onClick={handleCancelEdits} disabled={saving}>
                  Cancel
                </Button>
              </Group>
            </Stack>
          )}

          {isSent && (
            <Stack gap="sm">
              <Text size="xs" c="dimmed">
                Sent by {entry.sent_by_name ?? 'Unknown'} on{' '}
                {entry.sent_at ? formatDateTime(entry.sent_at) : '—'}
                {entry.template_name ? ` · Template: ${entry.template_name}` : ''}
              </Text>
              {entry.coach_name && (
                <Text size="xs" c="dimmed">
                  CC: {entry.coach_name}
                  {entry.coach_email ? ` (${entry.coach_email})` : ''}
                </Text>
              )}
              <Box>
                <Text size="xs" fw={600} c="dimmed" mb={4} tt="uppercase">
                  Subject
                </Text>
                <Box
                  p="xs"
                  style={{
                    background: '#F5F3EF',
                    borderRadius: 6,
                    border: '1px solid #E8E6E1',
                  }}
                >
                  <Text size="sm">{entry.rendered_subject ?? '(empty)'}</Text>
                </Box>
              </Box>
              <Box>
                <Text size="xs" fw={600} c="dimmed" mb={4} tt="uppercase">
                  Body
                </Text>
                <Box
                  p="sm"
                  style={{
                    background: '#F5F3EF',
                    borderRadius: 6,
                    border: '1px solid #E8E6E1',
                    fontSize: 13,
                    lineHeight: 1.55,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: entry.rendered_body ? sanitizeHtml(entry.rendered_body) : '(empty)',
                  }}
                />
              </Box>
            </Stack>
          )}

          {isSkipped && (
            <Box
              p="sm"
              style={{
                background: '#F5F3EF',
                borderRadius: 6,
                border: '1px solid #E8E6E1',
              }}
            >
              <Text size="xs" c="dimmed">
                Skipped by {entry.skipped_by_name ?? 'Unknown'} on{' '}
                {entry.skipped_at ? formatDate(entry.skipped_at) : '—'}
              </Text>
              {entry.skip_note && (
                <Text size="sm" mt={6}>
                  &ldquo;{entry.skip_note}&rdquo;
                </Text>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function StatusLeadingIcon({ status }: { status: AttendanceFollowUpEntry['status'] }) {
  const size = 32;
  const iconSize = 16;
  const sharedStyle = {
    width: size,
    height: size,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as const;
  if (status === 'sent') {
    return (
      <Box style={{ ...sharedStyle, background: '#E8F0EA', color: '#4A7C59' }}>
        <CheckCircle size={iconSize} />
      </Box>
    );
  }
  if (status === 'skipped') {
    return (
      <Box style={{ ...sharedStyle, background: '#EEEEEA', color: '#888' }}>
        <XCircle size={iconSize} />
      </Box>
    );
  }
  return (
    <Box
      style={{
        ...sharedStyle,
        background: 'var(--medley-flag-bg)',
        color: 'var(--medley-flag-text)',
      }}
    >
      <Mail size={iconSize} />
    </Box>
  );
}

function StatusPill({ status }: { status: AttendanceFollowUpEntry['status'] }) {
  if (status === 'sent') {
    return (
      <Badge size="sm" color="medleyGreen" variant="light">
        SENT
      </Badge>
    );
  }
  if (status === 'skipped') {
    return (
      <Badge size="sm" color="gray" variant="light">
        SKIPPED
      </Badge>
    );
  }
  if (status === 'no_template') {
    return (
      <Badge size="sm" color="orange" variant="light">
        NO TEMPLATE
      </Badge>
    );
  }
  return (
    <Badge
      size="sm"
      variant="filled"
      styles={{
        root: {
          background: 'var(--medley-flag-bg)',
          color: 'var(--medley-flag-text)',
        },
      }}
    >
      PENDING
    </Badge>
  );
}

function buildSecondaryLine(entry: AttendanceFollowUpEntry): string {
  if (entry.status === 'pending' || entry.status === 'no_template') {
    const abs = `${entry.cumulative_absence_count}${ordinalSuffix(entry.cumulative_absence_count)} ${entry.absence_type}`;
    const tier = entry.template_name ? ` · ${entry.template_name}` : '';
    return `${abs}${tier}`;
  }
  if (entry.status === 'sent') {
    const dateLabel = entry.sent_at ? `Sent ${formatDate(entry.sent_at)}` : 'Sent';
    const tier = entry.template_name ? ` · ${entry.template_name}` : '';
    return `${dateLabel}${tier}`;
  }
  // skipped
  const dateLabel = entry.skipped_at ? `Skipped ${formatDate(entry.skipped_at)}` : 'Skipped';
  const preview = entry.skip_note ? ` — "${truncate(entry.skip_note, 60)}"` : '';
  return `${dateLabel}${preview}`;
}

function buildTemplateOptions(
  templates: AbsenceEmailTemplate[],
  suggestedTemplateId: string | null,
): Array<{ group: string; items: Array<{ value: string; label: string }> }> {
  const byTier = new Map<string, AbsenceEmailTemplate[]>();
  for (const t of templates) {
    if (!t.is_active) continue;
    const list = byTier.get(t.escalation_tier) ?? [];
    list.push(t);
    byTier.set(t.escalation_tier, list);
  }
  return TIER_ORDER.filter(tier => byTier.has(tier)).map(tier => ({
    group: TIER_LABELS[tier] ?? tier,
    items: (byTier.get(tier) ?? [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(t => ({
        value: t.id,
        label: t.id === suggestedTemplateId ? `${t.name} (suggested)` : t.name,
      })),
  }));
}

function ordinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

function formatSessionDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'numeric',
    day: 'numeric',
  });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export default FollowUpAccordionRow;
